import type { ReactNode } from "react";
import type { Lesson } from "@edu/shared";
import { KindMark } from "../schedule/EntryKind.tsx";
import { callWhoOf } from "../calls/call-row.ts";

/**
 * Колонка «Ученик» в строке урока: имя, название группы или пустота.
 *
 * ⚠️ **У занятия группы имени в этой колонке НЕТ, и подставить одного из
 * двенадцати нельзя** (раздел 14 `docs/group-room-design.md`). Строка описывает
 * занятие целиком: сервер схлопывает N уроков одной комнаты в одну строку
 * (2.3 того же проекта) и вместо ученика присылает название группы. Поставив
 * сюда имя опорного участника, мы сказали бы, что занятие было с ним одним, и
 * так же подписали бы разбор в кабинете.
 *
 * ⚠️ **Вид занятия читается ПОЛЕМ `kind`, а не по наличию `group`.** Название
 * группы стоит двух join, и список, который за них не заплатил, всё равно
 * обязан отличить занятие группы от личного: иначе на групповом занятии встанет
 * фигура одного человека. Ровно этот промах чинился 02.09.2026 в сетке
 * расписания.
 *
 * ⚠️ Свой файл, а не копия в каждом списке: колонку рисуют трое (архив, список
 * уроков и уроки заготовки), а три редакции одной колонки человек видит на
 * соседних экранах. Третий приехал 02.09.2026 вместе со свёрткой: до неё он
 * читал `studentDisplayName` напрямую, и у занятия группы это давало серое «без
 * ученика» вместо названия группы.
 *
 * ⚠️ Слот занят во всех случаях, даже когда сказать нечего: пустая ячейка
 * схлопнулась бы, и соседние колонки строки уехали бы влево. Та же причина, что
 * у `PlanCell`.
 */
export function LessonWho({ lesson, main = false }: { lesson: Lesson; main?: boolean }) {
  /*
    Звонок с преподавателями первым: ученика у него нет по устройству, и «без
    ученика» читалось бы как недоделанный урок (9.4
    `docs/teacher-calls-design.md`). Подпись одна у ведущего и у гостя
  */
  const call = callWhoOf(lesson);
  if (call !== null) return <Cell main={main}>{call}</Cell>;

  if (lesson.kind === "group") {
    const group = lesson.group;
    if (group === undefined) {
      /*
        Занятие группы без названия: строку свернули, а имя группы не приехало.
        Сегодня недостижимо (сеанс держит группу внешним ключом), но пустая
        ячейка тут хуже короткого слова.
      */
      return (
        <Cell main={main} empty>
          группа
        </Cell>
      );
    }
    return (
      <Cell main={main}>
        <KindMark kind="group" size={group.size} />
        {group.title}
      </Cell>
    );
  }

  if (lesson.studentDisplayName != null) {
    return <Cell main={main}>{lesson.studentDisplayName}</Cell>;
  }

  return (
    <Cell main={main} empty>
      без ученика
    </Cell>
  );
}

/**
 * Сама ячейка: класс и подпись слота во всех случаях сразу.
 *
 * ⚠️ **Главной колонка бывает не везде, и это не украшение.** В списке уроков
 * заготовки на «кто» держится вся различимость строк (по названию уроки одной
 * заготовки не различаются, а по имени различаются всегда), поэтому там она
 * первая и жирная; в архиве и в списке уроков первой стоит название урока.
 *
 * ⚠️ Подпись слота (`data-label`; ниже порога колонок её показывают вместо
 * шапки те списки, которые ещё не переехали на узкую строку Р3) у главной
 * ячейки не ставится ни на одном списке: она идёт первой строкой карточки, и
 * подписывать в ней нечего.
 *
 * ⚠️ **`data-narrow="sub"` говорит, что на узкой строке эта колонка становится
 * подписью под именем**, а не прячется вместе с прочими слотами. Держит её то
 * же, что делает её главной в списке уроков заготовки: по названию занятия
 * одного дня часто не различаются вовсе, а по тому, с кем они, различаются
 * всегда. Метка стоит здесь, а не в строке списка, потому что решение это про
 * КОЛОНКУ и одинаково верно везде, где её рисуют; работает она только там, где
 * список объявил себя узкой строкой (`data-rows` на `.list`), поэтому у архива
 * и у уроков заготовки сегодня не меняет ничего.
 */
function Cell(props: { main: boolean; empty?: boolean; children: ReactNode }) {
  const cls =
    "cell" +
    (props.main ? " row__name" : "") +
    (props.empty === true ? " row__void" : "");
  return (
    <span
      className={cls}
      {...(props.main ? {} : { "data-label": "Ученик", "data-narrow": "sub" })}
    >
      {props.children}
    </span>
  );
}
